import { SPOTS } from "./appData";
import { applyCarryOver, applyPendingCarryOver, carryOverMessage, sumAdded } from "./carryOverCore";

/**
 * 旧 URL（dokoiku-fukuoka.vercel.app）から運ばれてきたお気に入り・履歴・保存したプランを取り込む（Web のみ）。
 * 仕組みと外す時期は carryOverCore.js の冒頭を参照。
 *
 * 荷物の届き方は 2 通り: アドレス欄の `#dokoiku-carry=`（applyCarryOver）と、
 * /carry.html が localStorage に預けた分（applyPendingCarryOver。トップ以外の旧 URL から来た人）。
 *
 * App.js の `import "./carryOver";` で読み込まれる。モジュールの評価時（＝App が最初に描画され、
 * AsyncStorage から保存データを読むより前）に localStorage へ合わせておくので、App.js 側は何も変えずに
 * 引き継いだデータを読む（Web の AsyncStorage は localStorage をそのまま使う）。
 *
 * 取り込めたら画面の下に案内を 1 回だけ出す。React の外で DOM に直接置くのは、App.js を触らずに済ませるため。
 * ネイティブは carryOver.native.js（何もしない）。
 */

const TOAST_ID = "dokoiku-carry-toast";
const TOAST_MS = 9000;

function showToast(message) {
  if (typeof document === "undefined") return;
  const mount = () => {
    if (!document.body || document.getElementById(TOAST_ID)) return;
    const reduceMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const box = document.createElement("div");
    box.id = TOAST_ID;
    box.setAttribute("role", "status");
    box.setAttribute("aria-live", "polite");
    Object.assign(box.style, {
      position: "fixed",
      left: "50%",
      top: "max(16px, env(safe-area-inset-top))",
      transform: reduceMotion ? "translateX(-50%)" : "translate(-50%, -12px)",
      opacity: reduceMotion ? "1" : "0",
      transition: reduceMotion ? "none" : "opacity .25s ease, transform .25s ease",
      zIndex: "2147483000",
      width: "calc(100% - 32px)",
      maxWidth: "440px",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      padding: "14px 12px 14px 16px",
      borderRadius: "16px",
      background: "#1E3358",
      color: "#FFFFFF",
      boxShadow: "0 12px 32px rgba(27, 43, 75, 0.28)",
      fontFamily: "inherit",
      lineHeight: "1.55",
    });

    const icon = document.createElement("span");
    icon.textContent = "📦";
    icon.setAttribute("aria-hidden", "true");
    Object.assign(icon.style, { fontSize: "22px", lineHeight: "1.2", flex: "0 0 auto" });

    const text = document.createElement("div");
    Object.assign(text.style, { flex: "1 1 auto", minWidth: "0" });
    const title = document.createElement("div");
    title.textContent = message.title;
    Object.assign(title.style, { fontWeight: "800", fontSize: "15px" });
    const body = document.createElement("div");
    body.textContent = message.body;
    Object.assign(body.style, { fontSize: "14px", marginTop: "2px" });
    const hint = document.createElement("div");
    hint.textContent = message.hint;
    Object.assign(hint.style, { fontSize: "12px", marginTop: "4px", color: "#D6DEEC" });
    text.append(title, body, hint);

    const close = document.createElement("button");
    close.type = "button";
    close.textContent = "×";
    close.setAttribute("aria-label", "この案内を閉じる");
    Object.assign(close.style, {
      flex: "0 0 auto",
      width: "40px",
      height: "40px",
      marginTop: "-8px",
      border: "0",
      borderRadius: "10px",
      background: "transparent",
      color: "#FFFFFF",
      fontSize: "22px",
      lineHeight: "1",
      cursor: "pointer",
    });

    let timer = null;
    const dismiss = () => {
      clearTimeout(timer);
      if (reduceMotion) {
        box.remove();
        return;
      }
      box.style.opacity = "0";
      box.style.transform = "translate(-50%, -12px)";
      setTimeout(() => box.remove(), 260);
    };
    close.addEventListener("click", dismiss);
    // 読んでいる途中で消えないよう、触れている間は止める。
    box.addEventListener("mouseenter", () => clearTimeout(timer));
    box.addEventListener("mouseleave", () => {
      clearTimeout(timer);
      timer = setTimeout(dismiss, 4000);
    });

    box.append(icon, text, close);
    document.body.appendChild(box);
    if (!reduceMotion) {
      // requestAnimationFrame は裏のタブでは止まるので使わない。レイアウトを 1 回読ませてから切り替える。
      box.getBoundingClientRect();
      box.style.opacity = "1";
      box.style.transform = "translateX(-50%)";
    }
    // 裏のタブで開かれたときは、見える状態になってから時間を数える（見ないうちに消えないように）。
    const startTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(dismiss, TOAST_MS);
    };
    if (document.visibilityState === "hidden") {
      const onVisible = () => {
        if (document.visibilityState === "hidden") return;
        document.removeEventListener("visibilitychange", onVisible);
        startTimer();
      };
      document.addEventListener("visibilitychange", onVisible);
    } else {
      startTimer();
    }
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", mount, { once: true });
  else setTimeout(mount, 400);
}

function run() {
  if (typeof window === "undefined" || !window.location) return;
  let storage;
  try {
    storage = window.localStorage;
  } catch (error) {
    storage = null;
  }
  if (!storage) {
    if (window.location.hash.includes("dokoiku-carry=")) {
      console.warn("[引き継ぎ] この端末では保存領域が使えないため、前の住所のデータを取り込めません");
      try {
        window.history.replaceState(window.history.state ?? null, "", `${window.location.pathname}${window.location.search}`);
      } catch (error) {
        // アドレス欄を直せなくても表示には影響しない
      }
    }
    return;
  }
  const options = { storage, logger: console, knownIds: new Set(SPOTS.map((spot) => spot.id)) };
  // アドレス欄で直接届いた荷物と、carry.html が預かっていた荷物（トップ以外のページ経由で来た人）の両方を見る。
  const fromHash = applyCarryOver({ location: window.location, history: window.history, ...options });
  const fromPending = applyPendingCarryOver(options);
  const message = carryOverMessage(sumAdded(fromHash, fromPending));
  if (message) showToast(message);
}

try {
  run();
} catch (error) {
  // 引き継ぎに失敗してもアプリ本体は必ず起動させる。
  console.warn("[引き継ぎ] 前の住所のデータを取り込めませんでした", error);
}
